import React, { useEffect, useMemo } from 'react';
import axios from 'axios';

import { useCmsEmbedded } from '~/components/AiChat/hooks/useCmsEmbedded';
import type { ActionFooterProps } from '~/components/AiChat/SideBarDialog/Actions/Action/ActionTypes';
import { InvoiceExecuteButton } from '~/components/AiChat/SideBarDialog/Actions/Action/InvoiceAnalysis/InvoiceExecuteButton';
import { InvoiceUnexecuteButton } from '~/components/AiChat/SideBarDialog/Actions/Action/InvoiceAnalysis/InvoiceUnexecuteButton';
import { InvoiceRecommendationAnalysis } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis';
import type {
  InvoiceAnalysisResponse,
  InvoiceData,
  LineItemProps,
} from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/types';
import type { InvoiceAnalysisRecommendationTaskData } from '~/components/AiChat/types';
import { AI_ACTION_TYPES } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';
import mixpanel from '~/components/CmsMain/mixpanel';
import { useInvoice } from '~/components/invoices/hooks/useInvoice';
import { mapInvoiceToInvoiceData, mapLineItemToLineItemProps } from '~/components/invoices/invoiceUtils';
import { CLIVE_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { SaveInvoiceRequestInvoiceProcessingType } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

const INVOICE_PROCESSING_ODIE_INVOICE = SaveInvoiceRequestInvoiceProcessingType.OdieInvoice;

export const InvoiceAnalysisFooter: React.FC<ActionFooterProps> = ({ action, onExecute, onUnexecute, isDone }) => {
  const action_data = action.task_data as InvoiceAnalysisRecommendationTaskData;

  const [isOpen, setIsOpen] = React.useState(false);
  const [startTimeout, setStartTimeout] = React.useState<number>(Date.now());
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [filePublicUrl, setFilePublicUrl] = React.useState<string>();
  const { applyInvoiceItem, coInsuranceRates, useFetchExposuresProxy } = useCmsEmbedded();
  const { isExposuresLoading, isExposuresError, exposures } = useFetchExposuresProxy();
  const [isFileLoadingError, setIsFileLoadingError] = React.useState(false);
  const { claim, onClaimUpdate } = useClaim({ claimRequired: true });

  const {
    invoice,
    isLoading: isInvoiceLoading,
    isError: isInvoiceError,
  } = useInvoice({
    documentSourceId: action_data.claim_document_source_id,
  });

  useEffect(() => {
    const fetchFilePublicUrl = async () => {
      try {
        const { data } = await axios.get(
          `/api/v1/claims/${claim.id}/chat/documents/${action_data.claim_document_source_id}/public_file`
        );
        setFilePublicUrl(data?.file_public_url || null);
      } catch (error) {
        await reportAxiosError(error);
        setIsFileLoadingError(true);
      }
    };

    if (!filePublicUrl) {
      action_data.claim_document_source_id ? fetchFilePublicUrl() : setFilePublicUrl(action_data.file_path);
    }
  }, [action_data.file_path, claim.id, action_data.claim_document_source_id, filePublicUrl]);

  const isLoadingError = isExposuresError || isFileLoadingError || isInvoiceError;

  const isLoading = isInvoiceLoading || isExposuresLoading;
  const exposuresByCoverageKey = Object.fromEntries(exposures.map((exposure) => [exposure.coverage_type, exposure]));
  const coverages = (action_data.coverages || []).map(({ coverage_display_name, coverage_key }) => ({
    description: coverage_display_name,
    value: coverage_key,
  }));

  const coverageMap = useMemo(() => {
    return coverages.reduce(
      (acc, { value, description }) => {
        acc[value] = description;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [coverages]);

  const mapRecommendationToInvoiceData = (data: InvoiceAnalysisRecommendationTaskData): InvoiceData => ({
    date: data.invoice_date,
    number: data.invoice_number,
    vendor: data.vendor_name,
    document_claim_internal_id: data.document_claim_internal_id,
  });

  const invoiceData = useMemo((): InvoiceData => {
    if (invoice) {
      const document = claim.documents?.find((d) => d.id === invoice.document_id);
      if (!document) {
        return mapRecommendationToInvoiceData(action_data);
      }
      return mapInvoiceToInvoiceData(document, invoice);
    }
    return mapRecommendationToInvoiceData(action_data);
  }, [invoice, claim.documents, action_data]);

  const lineItems = useMemo((): LineItemProps[] => {
    if (invoice) {
      return invoice.line_items.map(mapLineItemToLineItemProps);
    }
    return action_data.line_items.map(
      (item: InvoiceAnalysisRecommendationTaskData['line_items'][0]): LineItemProps => ({
        id: item.id,
        description: item.description,
        coverage_decision: item.coverage_decision,
        coverage: coverageMap[item.coverage_key || ''] ? item.coverage_key : '',
        amount: item.total_price ?? 0,
        covered_amount: item.covered_amount, // NOTE: DONT override with 0 to differentiate between decided 0 coverage and not decided yet
        explanation: item.explanation,
      })
    );
  }, [invoice, action_data.line_items, coverageMap]);

  const handleAction = async (data: InvoiceAnalysisResponse) => {
    try {
      setIsSubmitting(true);

      const invoiceAnalysisUpdate = { ...data, task_id: action_data.task_id };
      invoiceAnalysisUpdate.task_id = action_data.task_id;
      invoiceAnalysisUpdate.line_items = invoiceAnalysisUpdate.line_items.map((item) => ({
        ...item,
        coverage_key: item.coverage || null,
      }));

      await axios.post(`/api/v1/claims/${claim.id}/invoice_based_da/ai_save_invoice`, {
        invoice_analysis: invoiceAnalysisUpdate,
        invoice_document_source_id: action_data.claim_document_source_id,
        analysis_results_id: action_data.analysis_results_id,
        invoice_number: data.invoice_data.number,
        invoice_date: data.invoice_data.date,
        vendor: data.invoice_data.vendor,
        invoice_processing_type: INVOICE_PROCESSING_ODIE_INVOICE,
      });

      await onClaimUpdate();
    } catch (error) {
      await reportAxiosError(error);

      throw Error('Failed to update invoice analysis results');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);

    mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_INVOICE_PAGE_VIEWED, {
      time: Date.now() - startTimeout,
    });
  };

  if (isLoading) return null;

  return (
    <div className="flex">
      <div className="ml-auto flex gap-8">
        {isDone && (
          <InvoiceUnexecuteButton
            onUnexecute={() => {
              mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_UNEXECUTE_CLICKED, {
                is_done: isDone,
                type: AI_ACTION_TYPES.INVOICE_ANALYSIS_RECOMMENDATION,
              });

              if (onUnexecute) {
                onUnexecute();
              }
            }}
          />
        )}
        <InvoiceExecuteButton
          onExecute={() => {
            setIsOpen(true);
            setStartTimeout(Date.now());

            mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_OPEN_CLICKED, {
              is_done: isDone,
              type: AI_ACTION_TYPES.INVOICE_ANALYSIS_RECOMMENDATION,
            });
          }}
          isDone={isDone}
        />
      </div>
      <InvoiceRecommendationAnalysis
        disabled={isDone || isSubmitting || isLoadingError}
        submitting={isSubmitting}
        open={isOpen}
        dialogTitle={`Claim ${claim.claim_id_display}`}
        invoiceData={invoiceData}
        lineItems={lineItems}
        coverages={coverages}
        exposuresByCoverageKey={exposuresByCoverageKey}
        filePath={filePublicUrl}
        onClose={handleClose}
        onSave={async (data): Promise<boolean> => {
          try {
            await handleAction(data);
            return true;
          } catch (error) {
            return false;
          }
        }}
        onSaveAndFinish={async (data): Promise<boolean> => {
          try {
            await handleAction(data);
            if (onExecute) onExecute();
            return true;
          } catch (error) {
            return false;
          }
        }}
        onApplyCoveredItem={async (item, onUpdate) => {
          try {
            setIsSubmitting(true);
            await applyInvoiceItem({
              item,
              claimDocumentSourceId: action_data.claim_document_source_id,
              onUpdate,
            });
          } finally {
            setIsSubmitting(false);
          }
        }}
        coInsuranceCoverageRates={coInsuranceRates}
      />
    </div>
  );
};
